import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_NUM_COMPONENTS } from '@/tools/partialLeastSquares/partialLeastSquares.constants';
import type { BaseToolPersistedState, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { Capsule } from '@/utilities/datetime.constants';

interface PartialLeastSquaresState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any;
  modelPropertiesExpanded: boolean;
  color: string;
  numComponents: number;
  scale: boolean;
  maxIterations: number;
  tolerance: number;
}

export type PartialLeastSquaresDehydratedState = Omit<PartialLeastSquaresState, 'window'>;

export class PartialLeastSquaresStore extends BaseToolStore {
  static readonly storeName = 'sqPartialLeastSquaresStore';
  type = TREND_TOOLS.PARTIAL_LEAST_SQUARES;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    targetSignal: { predicate: ['name', 'target'] },
    inputSignals: {
      predicate: (parameter: ToolParameter) =>
        !_.includes(['target', 'condition', 'restrictToCondition'], parameter.name ?? ''),
      multiple: true,
    },
  };

  initialize(): void {
    const initialState: PartialLeastSquaresState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numComponents: DEFAULT_NUM_COMPONENTS,
      scale: true,
      maxIterations: 500,
      tolerance: 1e-6,
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] | undefined {
    return this.state.get('inputSignals');
  }

  get targetSignal(): ToolItem | undefined {
    return this.state.get('targetSignal');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numComponents(): number {
    return this.state.get('numComponents');
  }

  get scale(): boolean {
    return this.state.get('scale');
  }

  get maxIterations(): number {
    return this.state.get('maxIterations');
  }

  get tolerance(): number {
    return this.state.get('tolerance');
  }

  dehydrate(): PartialLeastSquaresDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<PartialLeastSquaresDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setScale(payload: { scale: boolean }): void {
    this.state.set('scale', payload.scale);
  }

  setMaxIterations(payload: { maxIterations: number }): void {
    this.state.set('maxIterations', payload.maxIterations);
  }

  setNumComponents(payload: { numComponents: number }): void {
    this.state.set('numComponents', payload.numComponents);
  }

  setTolerance(payload: { tolerance: number }): void {
    this.state.set('tolerance', payload.tolerance);
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  override modifyConfigParams<TConfig extends Record<string, unknown>>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    PLS_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PLS_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    PLS_SET_NUM_COMPONENTS: this.setNumComponents,
    PLS_SET_SCALE: this.setScale,
    PLS_SET_MAX_ITERATIONS: this.setMaxIterations,
    PLS_SET_TOLERANCE: this.setTolerance,
    PLS_SET_COLOR: this.setColor,
  };
}
